import type { User } from '../types/auth';
import type {
  AttendanceRecord,
  BranchLocation,
  ClassOffering,
  ClassSession,
  Enrollment,
  EventRegistration,
  InstituteEvent,
  LeaveRequest,
} from '../types/academic';
import type { NotificationRecord } from '../types/notifications';
import type { Announcement } from '../types/announcements';
import type {
  BillingSummary,
  CoachAgreement,
  LedgerEntry,
  StudentInvoice,
} from '../types/billing';

const now = Math.floor(Date.now() / 1000);
const todayStart = () => {
  const d = new Date();
  d.setHours(10, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
};

export const DEMO_USERS: User[] = [
  {
    _id: 'demo-owner-001',
    email: 'owner@dancemance.com',
    name: 'Owner Dancemance',
    role: 'owner',
    roles: ['owner', 'coach', 'student'],
    status: 'active',
    phone: '09120000000',
    honorific: 'mr',
    photoFileId: 'demo-photo-owner',
    photoUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" rx="60" fill="%231e3a2f"/><circle cx="60" cy="48" r="22" fill="%23e2b17a"/><path d="M40 38c2-10 10-16 20-16s18 6 20 16c-3 1-8 0-11-2-4-2-7-2-9 0-3 2-8 3-11 2z" fill="%23475569"/><path d="M26 104c4-24 18-35 34-35s30 11 34 35z" fill="%230f172a"/><polygon points="60,70 54,85 66,85" fill="%23e2b17a"/><polygon points="56,84 64,84 60,104" fill="%23059669"/><circle cx="53" cy="47" r="2" fill="%231e293b"/><circle cx="67" cy="47" r="2" fill="%231e293b"/></svg>',
  },
  {
    _id: 'demo-admin-001',
    email: 'admin@dancemance.com',
    name: 'Sara Admin',
    role: 'admin',
    roles: ['admin', 'student'],
    status: 'active',
    phone: '09120000001',
    honorific: 'ms',
    photoFileId: 'demo-photo-admin',
    photoUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" rx="60" fill="%232e2640"/><circle cx="60" cy="48" r="21" fill="%23fbcfe8"/><path d="M39 46c0-13 9-23 21-23s21 10 21 23c0 9-4 18-7 22-3-7-8-11-14-11s-11 4-14 11c-3-4-7-13-7-22z" fill="%233b0764"/><path d="M28 104c4-22 17-33 32-33s28 11 32 33z" fill="%23581c87"/><circle cx="54" cy="48" r="2" fill="%231e293b"/><circle cx="66" cy="48" r="2" fill="%231e293b"/><path d="M56 57q4 3 8 0" stroke="%23a21caf" stroke-width="2" fill="none" stroke-linecap="round"/></svg>',
  },
  {
    _id: 'demo-coach-001',
    email: 'coach@dancemance.com',
    name: 'Reza Coach',
    role: 'coach',
    roles: ['coach', 'student'],
    status: 'active',
    phone: '09120000002',
    honorific: 'mr',
    photoFileId: 'demo-photo-coach-reza',
    photoUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" rx="60" fill="%232b394a"/><circle cx="60" cy="48" r="22" fill="%23e0ac69"/><path d="M42 40c0-10 8-18 18-18s18 8 18 18c0 2-2 4-5 4s-7-3-13-3-10 3-13 3-5-2-5-4z" fill="%23201a18"/><path d="M26 104c4-22 18-34 34-34s30 12 34 34z" fill="%231e293b"/><path d="M52 70l8 12 8-12z" fill="%23e0ac69"/><circle cx="53" cy="48" r="2" fill="%231f2937"/><circle cx="67" cy="48" r="2" fill="%231f2937"/><path d="M56 56q4 3 8 0" stroke="%239a3412" stroke-width="2" fill="none" stroke-linecap="round"/></svg>',
  },
  {
    _id: 'demo-coach-002',
    email: 'sara.coach@dancemance.com',
    name: 'Sara Coach',
    role: 'coach',
    roles: ['coach', 'student'],
    status: 'active',
    phone: '09120000005',
    honorific: 'ms',
    photoFileId: 'demo-photo-coach-sara',
    photoUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" rx="60" fill="%234a2835"/><circle cx="60" cy="49" r="21" fill="%23fcd34d"/><path d="M38 48c0-14 10-25 22-25s22 11 22 25c0 10-3 18-6 22-4-8-10-13-16-13s-12 5-16 13c-3-4-6-12-6-22z" fill="%231c1917"/><path d="M28 104c4-20 16-32 32-32s28 12 32 32z" fill="%23831843"/><circle cx="53" cy="49" r="2" fill="%231f2937"/><circle cx="67" cy="49" r="2" fill="%231f2937"/><path d="M55 57q5 4 10 0" stroke="%23be123c" stroke-width="2.2" fill="none" stroke-linecap="round"/><circle cx="39" cy="53" r="3" fill="%23fbbf24"/><circle cx="81" cy="53" r="3" fill="%23fbbf24"/></svg>',
  },
  {
    _id: 'demo-student-001',
    email: 'student@dancemance.com',
    name: 'Ali Student',
    role: 'student',
    roles: ['student'],
    status: 'active',
    phone: '09120000003',
    honorific: 'mr',
    packIcon: 'dancer',
    photoFileId: 'demo-photo-student-ali',
    photoUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" rx="60" fill="%23164e63"/><circle cx="60" cy="48" r="21" fill="%23fcd34d"/><path d="M42 42c0-11 8-19 18-19s18 8 18 19c-3 1-8 1-11-1-4-2-6-2-8 0-3 2-7 2-10 1z" fill="%231e293b"/><path d="M26 104c4-22 18-33 34-33s30 11 34 33z" fill="%230e7490"/><circle cx="53" cy="48" r="2" fill="%231f2937"/><circle cx="67" cy="48" r="2" fill="%231f2937"/><path d="M56 56q4 3 8 0" stroke="%230e7490" stroke-width="2" fill="none" stroke-linecap="round"/></svg>',
  },
  {
    _id: 'demo-student-002',
    email: 'maria@dancemance.com',
    name: 'Maria Lopez',
    role: 'student',
    roles: ['student'],
    status: 'active',
    phone: '09120000004',
    honorific: 'ms',
    packIcon: 'heart',
    photoFileId: 'demo-photo-student-maria',
    photoUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" rx="60" fill="%23431407"/><circle cx="60" cy="48" r="21" fill="%23fed7aa"/><path d="M39 46c0-12 9-22 21-22s21 10 21 22c0 10-3 18-7 22-3-8-8-12-14-12s-11 4-14 12c-4-4-7-12-7-22z" fill="%237c2d12"/><path d="M28 104c4-22 17-33 32-33s28 11 32 33z" fill="%23ea580c"/><circle cx="54" cy="48" r="2" fill="%231e293b"/><circle cx="66" cy="48" r="2" fill="%231e293b"/><path d="M55 57q5 4 10 0" stroke="%23c2410c" stroke-width="2" fill="none" stroke-linecap="round"/></svg>',
  },
  {
    _id: 'demo-operator-001',
    email: 'operator@dancemance.com',
    name: 'Nima Operator',
    role: 'operator',
    roles: ['operator', 'student'],
    status: 'active',
    phone: '09120000006',
    honorific: 'mr',
  },
];

export const DEMO_LOCATIONS: BranchLocation[] = [
  {
    _id: 'demo-loc-001',
    name: 'سالن غرب',
    address: 'تهران، خیابان ولیعصر، پلاک ۱۲۳، سالن ۱',
    latitude: 35.7219,
    longitude: 51.3347,
    createdAt: now - 86400 * 40,
    updatedAt: now,
  },
  {
    _id: 'demo-loc-002',
    name: 'Main hall',
    address: 'تهران، سالن اصلی آموزشگاه',
    createdAt: now - 86400 * 40,
    updatedAt: now,
  },
];

export const DEMO_CLASSES: ClassOffering[] = [
  {
    _id: 'demo-class-001',
    name: 'Salsa Beginners',
    danceSportType: 'salsa',
    level: 'beginner',
    description: 'Foundation steps, rhythm, and partner connection for new dancers.',
    defaultDurationMinutes: 60,
    coachId: 'demo-coach-001',
    coachIds: ['demo-coach-001'],
    assistantIds: ['demo-coach-002'],
    status: 'active',
    registrationOpen: false,
    telegramGroupUrl: 'https://t.me/dancemance_salsa',
    branchLocationId: 'demo-loc-001',
    location: 'Salon1',
    pricing: { sessionsPerPaymentUnit: 4, pricePerUnit: 1_200_000, currency: 'IRT' },
    createdAt: now - 86400 * 30,
    updatedAt: now,
  },
  {
    _id: 'demo-class-002',
    name: 'Bachata Intermedio',
    danceSportType: 'bachata',
    level: 'intermediate',
    description: 'Turn patterns, body isolation, and social floor skills.',
    defaultDurationMinutes: 90,
    coachId: 'demo-coach-001',
    coachIds: ['demo-coach-001', 'demo-coach-002'],
    assistantIds: [],
    status: 'active',
    registrationOpen: false,
    telegramGroupUrl: 'https://t.me/dancemance_bachata',
    branchLocationId: 'demo-loc-001',
    location: 'Salon1',
    pricing: { sessionsPerPaymentUnit: 4, pricePerUnit: 1_600_000, currency: 'IRT' },
    createdAt: now - 86400 * 20,
    updatedAt: now,
  },
  {
    _id: 'demo-class-003',
    name: 'Friday Social',
    danceSportType: 'tango',
    level: 'open',
    description: 'Open-practice social — all levels welcome.',
    defaultDurationMinutes: 120,
    coachId: 'demo-coach-001',
    coachIds: ['demo-coach-001'],
    assistantIds: [],
    status: 'active',
    registrationOpen: true,
    telegramGroupUrl: 'https://t.me/dancemance_friday',
    branchLocationId: 'demo-loc-002',
    location: 'Main hall',
    createdAt: now - 86400 * 10,
    updatedAt: now,
  },
  {
    _id: 'demo-class-004',
    name: 'Salsa Avanzado',
    danceSportType: 'salsa',
    level: 'advanced',
    description: 'Advanced shines, complex turn patterns, and musicality.',
    defaultDurationMinutes: 75,
    coachId: 'demo-coach-001',
    coachIds: ['demo-coach-001'],
    assistantIds: [],
    status: 'active',
    registrationOpen: true,
    telegramGroupUrl: 'https://t.me/dancemance_salsa_adv',
    branchLocationId: 'demo-loc-001',
    location: 'Salon1',
    pricing: { sessionsPerPaymentUnit: 4, pricePerUnit: 1_800_000, currency: 'IRT' },
    createdAt: now - 86400 * 5,
    updatedAt: now,
  },
  {
    _id: 'demo-class-005',
    name: 'Salsa Fundamentals',
    danceSportType: 'salsa',
    level: 'beginner',
    description: 'Basics and timing for new salsa students.',
    defaultDurationMinutes: 60,
    coachId: 'demo-coach-001',
    coachIds: ['demo-coach-001'],
    assistantIds: [],
    status: 'active',
    registrationOpen: true,
    telegramGroupUrl: 'https://t.me/dancemance_salsa_beg',
    branchLocationId: 'demo-loc-001',
    location: 'Salon1',
    pricing: { sessionsPerPaymentUnit: 4, pricePerUnit: 1_200_000, currency: 'IRT' },
    createdAt: now - 86400 * 4,
    updatedAt: now,
  },
  {
    _id: 'demo-class-006',
    name: 'Bachata Masterclass',
    danceSportType: 'bachata',
    level: 'advanced',
    description: 'Sensual bachata technique and advanced styling.',
    defaultDurationMinutes: 90,
    coachId: 'demo-coach-001',
    coachIds: ['demo-coach-001'],
    assistantIds: [],
    status: 'active',
    registrationOpen: true,
    telegramGroupUrl: 'https://t.me/dancemance_bachata_adv',
    branchLocationId: 'demo-loc-001',
    location: 'Salon1',
    pricing: { sessionsPerPaymentUnit: 4, pricePerUnit: 1_900_000, currency: 'IRT' },
    createdAt: now - 86400 * 3,
    updatedAt: now,
  },
];

export const DEMO_ENROLLMENTS: Enrollment[] = [
  {
    _id: 'demo-enroll-001',
    studentId: 'demo-student-001',
    classId: 'demo-class-001',
    status: 'active',
    enrolledAt: now - 86400 * 14,
    startDate: now - 86400 * 14,
    entitlement: { granted: 4, consumed: 3, remaining: 1 },
    createdAt: now - 86400 * 14,
    updatedAt: now,
  },
  {
    _id: 'demo-enroll-002',
    studentId: 'demo-student-001',
    classId: 'demo-class-002',
    status: 'active',
    enrolledAt: now - 86400 * 7,
    startDate: now - 86400 * 7,
    entitlement: { granted: 4, consumed: 1, remaining: 3 },
    createdAt: now - 86400 * 7,
    updatedAt: now,
  },
  {
    _id: 'demo-enroll-003',
    studentId: 'demo-student-002',
    classId: 'demo-class-001',
    status: 'active',
    enrolledAt: now - 86400 * 5,
    startDate: now - 86400 * 5,
    entitlement: { granted: 4, consumed: 0, remaining: 4 },
    createdAt: now - 86400 * 5,
    updatedAt: now,
  },
];

export const DEMO_SESSIONS: ClassSession[] = [
  {
    _id: 'demo-session-001',
    classId: 'demo-class-001',
    scheduledStart: todayStart(),
    scheduledEnd: todayStart() + 3600,
    durationMinutes: 60,
    coachAssignment: { scheduledCoachId: 'demo-coach-001' },
    status: 'scheduled',
    teaching: {
      topics: ['Basic step', 'Cross body lead'],
      summary: 'Timing on 1-2-3, 5-6-7 and a clean slot change.',
      homework: 'Practice basic step 10 minutes daily.',
    },
    createdAt: now,
    updatedAt: now,
  },
  {
    _id: 'demo-session-002',
    classId: 'demo-class-002',
    scheduledStart: todayStart() + 7200,
    scheduledEnd: todayStart() + 7200 + 5400,
    durationMinutes: 90,
    coachAssignment: { scheduledCoachId: 'demo-coach-001' },
    status: 'scheduled',
    createdAt: now,
    updatedAt: now,
  },
  {
    _id: 'demo-session-003',
    classId: 'demo-class-003',
    scheduledStart: todayStart() + 14400,
    scheduledEnd: todayStart() + 14400 + 7200,
    durationMinutes: 120,
    coachAssignment: { scheduledCoachId: 'demo-coach-001' },
    status: 'scheduled',
    notes: 'Bring comfortable shoes',
    createdAt: now,
    updatedAt: now,
  },
];

export const DEMO_ATTENDANCE: AttendanceRecord[] = [
  {
    _id: 'demo-att-001',
    sessionId: 'demo-session-001',
    enrollmentId: 'demo-enroll-001',
    studentId: 'demo-student-001',
    status: 'present',
    recordedBy: 'demo-coach-001',
    recordedAt: now,
  },
  {
    _id: 'demo-att-002',
    sessionId: 'demo-session-004',
    enrollmentId: 'demo-enroll-001',
    studentId: 'demo-student-001',
    status: 'present',
    recordedBy: 'demo-coach-001',
    recordedAt: now - 86400 * 2,
  },
  {
    _id: 'demo-att-003',
    sessionId: 'demo-session-004',
    enrollmentId: 'demo-enroll-003',
    studentId: 'demo-student-002',
    status: 'on_leave',
    recordedBy: 'demo-coach-001',
    recordedAt: now - 86400 * 2,
  },
  {
    _id: 'demo-att-004',
    sessionId: 'demo-session-006',
    enrollmentId: 'demo-enroll-001',
    studentId: 'demo-student-001',
    status: 'absent',
    recordedBy: 'demo-coach-001',
    recordedAt: now - 86400 * 5,
  },
  {
    _id: 'demo-att-005',
    sessionId: 'demo-session-010',
    enrollmentId: 'demo-enroll-002',
    studentId: 'demo-student-001',
    status: 'absent',
    recordedBy: 'demo-coach-001',
    recordedAt: now - 86400 * 14,
  },
  {
    _id: 'demo-att-006',
    sessionId: 'demo-session-009',
    enrollmentId: 'demo-enroll-002',
    studentId: 'demo-student-001',
    status: 'on_leave',
    recordedBy: 'demo-admin-001',
    recordedAt: now - 86400 * 7,
  },
];

export const DEMO_LEAVE_REQUESTS: LeaveRequest[] = [
  {
    _id: 'demo-leave-001',
    studentId: 'demo-student-001',
    classId: 'demo-class-002',
    enrollmentId: 'demo-enroll-002',
    leaveStart: now - 86400 * 8,
    leaveEnd: now - 86400 * 6,
    noticeDays: 21,
    policyMinNoticeDays: 14,
    status: 'approved',
    reviewedBy: 'demo-admin-001',
    reviewedAt: now - 86400 * 18,
    createdAt: now - 86400 * 22,
    updatedAt: now - 86400 * 18,
  },
];

export const DEMO_EVENTS: InstituteEvent[] = [
  {
    _id: 'demo-event-001',
    name: 'Saturday Social Dance',
    eventType: 'social_dance',
    description: 'Open floor social — all levels. One session, no pack entitlement.',
    scheduledStart: todayStart() + 21600,
    scheduledEnd: todayStart() + 21600 + 7200,
    durationMinutes: 120,
    capacity: 40,
    branchLocationId: 'demo-loc-002',
    location: 'Main hall',
    responsibleUserId: 'demo-admin-001',
    responsibleName: 'Sara Admin',
    status: 'scheduled',
    createdAt: now - 86400 * 4,
    updatedAt: now,
  },
  {
    _id: 'demo-event-002',
    name: 'Bachata Practice Hour',
    eventType: 'practice',
    description: 'Guided practice for enrolled bachata students.',
    scheduledStart: now - 86400 * 3,
    scheduledEnd: now - 86400 * 3 + 3600,
    durationMinutes: 60,
    coachAssignment: {
      scheduledCoachId: 'demo-coach-001',
      actualCoachId: 'demo-coach-001',
    },
    status: 'completed',
    teaching: {
      topics: ['Musicality', 'Shine variations'],
      summary: 'Worked on hitting accents without breaking frame.',
    },
    createdAt: now - 86400 * 6,
    updatedAt: now - 86400 * 3,
  },
];

export const DEMO_EVENT_REGS: EventRegistration[] = [
  {
    _id: 'demo-ereg-001',
    eventId: 'demo-event-001',
    studentId: 'demo-student-001',
    status: 'registered',
  },
  {
    _id: 'demo-ereg-002',
    eventId: 'demo-event-002',
    studentId: 'demo-student-001',
    status: 'attended',
  },
];

export const DEMO_NOTIFICATIONS: NotificationRecord[] = [
  {
    _id: 'demo-note-001',
    title: 'Saturday social is open',
    body: 'Register for this week’s social dance — doors at 16:00.',
    channels: ['sms', 'email'],
    audience: 'all_students',
    status: 'sent',
    createdBy: 'demo-admin-001',
    sentAt: now - 3600,
    createdAt: now - 3600,
  },
];

export const DEMO_INVOICES: StudentInvoice[] = [
  {
    _id: 'demo-inv-001',
    enrollmentId: 'demo-enroll-001',
    classId: 'demo-class-001',
    className: 'Salsa Beginners',
    studentId: 'demo-student-001',
    studentName: 'هنرجو تستی ۱',
    invoiceNumber: 'DM-2026-0042',
    kind: 'topup',
    originalAmount: 1_500_000,
    discountAmount: 300_000,
    discountReason: 'تخفیف ثبت‌نام زودهنگام',
    amount: 1_200_000,
    currency: 'IRT',
    sessionsIncluded: 4,
    status: 'due',
    dueAt: now + 86400 * 3,
    createdAt: now - 86400,
  },
  {
    _id: 'demo-inv-002',
    enrollmentId: 'demo-enroll-002',
    classId: 'demo-class-002',
    className: 'Bachata Intermedio',
    studentId: 'demo-student-001',
    studentName: 'هنرجو تستی ۱',
    invoiceNumber: 'DM-2026-0038',
    kind: 'prepay',
    originalAmount: 2_000_000,
    discountAmount: 400_000,
    discountReason: 'تخفیف وفاداری هنرجو',
    amount: 1_600_000,
    currency: 'IRT',
    sessionsIncluded: 4,
    status: 'paid',
    dueAt: now - 86400 * 10,
    paidAt: now - 86400 * 8,
    createdAt: now - 86400 * 12,
  },
  {
    _id: 'demo-inv-003',
    enrollmentId: 'demo-enroll-001',
    classId: 'demo-class-001',
    className: 'Salsa Beginners',
    studentId: 'demo-student-001',
    studentName: 'هنرجو تستی ۱',
    invoiceNumber: 'DM-2026-0025',
    kind: 'prepay',
    originalAmount: 1_200_000,
    amount: 1_200_000,
    currency: 'IRT',
    sessionsIncluded: 4,
    status: 'paid',
    dueAt: now - 86400 * 40,
    paidAt: now - 86400 * 38,
    createdAt: now - 86400 * 42,
  },
];

export const DEMO_ANNOUNCEMENTS: Announcement[] = [
  {
    _id: 'demo-banner-001',
    message: 'کلاس سالسا فردا رأس ساعت ۱۸ برگزار می‌شود.',
    audience: 'class',
    classId: 'demo-class-001',
    className: 'Salsa Beginners',
    active: true,
    createdBy: 'demo-admin-001',
    createdAt: now,
  },
];

export const DEMO_AGREEMENTS: CoachAgreement[] = [
  {
    _id: 'demo-agree-001',
    coachId: 'demo-coach-001',
    kind: 'class_hourly',
    payType: 'fixed',
    staffKind: 'coach',
    rateIrt: 400_000,
    effectiveFrom: now - 86400 * 60,
  },
  {
    _id: 'demo-agree-002',
    coachId: 'demo-operator-001',
    kind: 'class_hourly',
    payType: 'percentage',
    staffKind: 'operator',
    percentage: 40,
    rateIrt: 0,
    effectiveFrom: now - 86400 * 60,
  },
];

export const DEMO_LEDGER: LedgerEntry[] = [
  {
    _id: 'demo-led-001',
    kind: 'income',
    source: 'vending',
    amountIrt: 850_000,
    period: 'daily',
    periodStart: now - 86400,
    note: 'Snack fridge + water',
    recordedBy: 'demo-operator-001',
    recordedByName: 'Nima Operator',
    share: {
      configId: 'demo-share-001',
      operatorPercentage: 40,
      institutePercentage: 60,
      operatorShareIrt: 340_000,
      instituteShareIrt: 510_000,
    },
    createdAt: now - 86400,
  },
  {
    _id: 'demo-led-002',
    kind: 'expense',
    source: 'supplies',
    amountIrt: 320_000,
    period: 'weekly',
    periodStart: now - 86400 * 3,
    note: 'Floor cleaner and towels',
    recordedBy: 'demo-admin-001',
    recordedByName: 'Sara Admin',
    createdAt: now - 86400 * 3,
  },
];

export const DEMO_BILLING_SUMMARY: BillingSummary = {
  sessionsPerPaymentUnit: 4,
  pricePerUnit: 1_200_000,
  currency: 'IRT',
  totalDue: 1_200_000,
  openInvoices: 1,
  nextPaymentSessionsRemaining: 1,
};

/** Demo sign-in credentials (any password works in demo mode) */
export const DEMO_CREDENTIALS_HINT = {
  owner: 'owner@dancemance.com',
  admin: 'admin@dancemance.com',
  coach: 'coach@dancemance.com',
  student: 'student@dancemance.com',
  operator: 'operator@dancemance.com',
};
